'use strict';

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var _Object$assign = _interopDefault(require('babel-runtime/core-js/object/assign'));
var $ = _interopDefault(require('jquery'));
var enquire = _interopDefault(require('enquire.js'));
var keyCodes = _interopDefault(require('../global/keycodes.js'));
var Breakpoints = _interopDefault(require('../global/breakpoints.js'));
var utils_js = require('../helpers/utils.js');

/**
 * Barebones JS accordion
 *
 * Defaults to mobile settings for now, since all accordions are currently mobile only
 *
 * @param {$DOM} el Element wrapper
 * @param {Object} [options] configuration options
 * @param {String} [options.itemSelector='.accordion-item--mobile'] The parent element for each accordion item
 * @param {String} [options.itemLink='.accordion-link'] Element to trigger accordion toggle
 * @param {String} [options.itemContent='.accordion-content'] Content to have visibility toggled
 * @param {Boolean} [options.mobileOnly=true] only works on mobile
 * @param {Boolean} [options.openFirst=true] Open first item by default
 * @param {Number} [options.slideSpeed=400] Slide speed duration in milliseconds. Automatically set to 0 if user has 'prefers-reduced-motion' set on their device
 * @class Accordion
 * @namespace ShopifyMarketing
 * @constructor
*/
function Accordion(el, options) {
  this.config = _Object$assign({
    itemSelector: '.accordion-item--mobile',
    itemLink: '.accordion-link',
    itemContent: '.accordion-content',
    mobileOnly: true,
    openFirst: true,
    slideSpeed: 400
  }, options);

  this.$el = utils_js.isJquery(el) ? el : $(el);

  if (!this.$el.length) {
    return false;
  }

  this.$accordionItems = this.$el.find(this.config.itemSelector);
  this.$accordionLinks = this.$el.find(this.config.itemLink);
  this.$accordionContents = this.$el.find(this.config.itemContent);

  this.toggle = this.toggle.bind(this);
  this.enable = this.enable.bind(this);
  this.disable = this.disable.bind(this);
  this.ensureTabIndex = this.ensureTabIndex.bind(this);
  this.removeTabIndex = this.removeTabIndex.bind(this);
  this.removeStyles = this.removeStyles.bind(this);
  this.setInitialAriaStates = this.setInitialAriaStates.bind(this);
  this.removeAriaStates = this.removeAriaStates.bind(this);
  this._onClick = this._onClick.bind(this);
  this._onKeydown = this._onKeydown.bind(this);

  if (this.config.mobileOnly) {
    enquire.register(Breakpoints.prototype.tablet, this.disable).register(Breakpoints.prototype.phone, this.enable);
  } else {
    this.enable();
  }
}

/**
 * @method getCurrentItemFromEvent
 * @param {Event} evt event object from click
 * @return {$ DOM element} currently selected item
*/
Accordion.prototype.getCurrentItemFromEvent = function (evt) {
  var $link = $(evt.currentTarget);
  return $link.closest(this.config.itemSelector);
};

/**
 * @method toggle
 * @param {$currentItem} currentItem Element to trigger accordion toggle
 * @param {Boolean} [doAnimation=true] Perform the animation, if user doesn't prefer reduced motion
 */
Accordion.prototype.toggle = function (currentItem) {
  var doAnimation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

  var $currentItem = utils_js.isJquery(currentItem) ? currentItem : $(currentItem);

  var newIsActiveState = !$currentItem.hasClass('js-is-active');
  var $itemLink = $currentItem.find(this.config.itemLink).first();
  var $itemContent = $currentItem.find(this.config.itemContent).first();

  // Check if user prefers reduced motion, and don't do slideToggle if true
  var prefersReducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  var isAnimationEnabled = prefersReducedMotion.matches ? false : doAnimation;

  // Toggle classes and aria states
  $currentItem.toggleClass('js-is-active', newIsActiveState);
  $itemContent.attr('aria-hidden', !newIsActiveState);
  $itemLink.attr('aria-expanded', newIsActiveState);

  // Maybe do animations
  if (isAnimationEnabled) {
    $itemContent.stop().slideToggle(this.config.slideSpeed).end();
  } else {
    // Native el.style.display is much faster than jQuery slideToggle(0) or .show()/.hide()
    $itemContent[0].style.display = newIsActiveState ? 'block' : 'none';
  }
};

Accordion.prototype.enable = function () {
  this.$el.on('click', this.config.itemLink, this._onClick);
  this.$el.on('keydown', this.config.itemLink, this._onKeydown);
  this.ensureTabIndex();
  this.setInitialAriaStates();

  if (this.config.openFirst) {
    this.openFirst();
  }

  this.$el.addClass('js-is-initialized');
};

Accordion.prototype.disable = function () {
  this.$el.off('click', this.config.itemLink);
  this.$el.off('keydown', this.config.itemLink);
  this.removeTabIndex();
  this.removeAriaStates();
  this.removeStyles();
  this.$el.removeClass('js-is-initialized');
};

Accordion.prototype.openFirst = function () {
  var $currentItem = this.$accordionItems.first().not('.js-is-active');

  this.toggle($currentItem, false);
};

Accordion.prototype.ensureTabIndex = function () {
  this.$accordionLinks.prop('tabindex', 0);
};

Accordion.prototype.removeTabIndex = function () {
  // removeProp('tabindex') actually just sets tabindex to 0
  this.$accordionLinks.removeAttr('tabindex');
};

Accordion.prototype.removeStyles = function () {
  this.$accordionItems.removeAttr('style');
  this.$accordionContents.removeAttr('style');
};

Accordion.prototype.setInitialAriaStates = function () {
  var config = this.config;

  this.$accordionItems.each(function (index, el) {
    var $el = $(el);
    var id = utils_js.uniqueId('Accordion');

    $el.removeClass('js-is-active');

    $el.find(config.itemContent).attr({
      'aria-hidden': 'true',
      id: id
    });

    $el.find(config.itemLink).attr({
      'aria-expanded': 'false',
      'aria-controls': id
    });
  });
};

Accordion.prototype.removeAriaStates = function () {
  this.$accordionContents.removeAttr('aria-hidden id');
  this.$accordionLinks.removeAttr('aria-controls aria-expanded');
};

Accordion.prototype._onKeydown = function (evt) {
  if (evt.keyCode !== keyCodes.ENTER) {
    return;
  }
  evt.preventDefault();

  this.toggle(this.getCurrentItemFromEvent(evt));
};

Accordion.prototype._onClick = function (evt) {
  evt.preventDefault();

  return this.toggle(this.getCurrentItemFromEvent(evt));
};

module.exports = Accordion;
